import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
// Importing Lucide React icons for UI components
import { BookOpen, Users, Award, Calendar, BookMarked, BrainCircuit, CheckCircle, MonitorPlay, MessageCircle, Mail, ArrowLeft, Check, X, Zap, Clock, Lightbulb, FileCheck, FileText, BarChart2, Target, User } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * MentorshipInfoPage Component
 * 
 * This component displays the general physics mentorship program information.
 * NOTE: This is different from the NEET-specific mentorship program described in the document.
 * Current page shows a 1-month general mentorship program, while NEET program is an 11-day strategic intervention.
 * 
 * Key Differences:
 * - Current: 1 month, 3 sessions, ₹4,500 - General physics mentorship
 * - NEET Program: 11 days, 10 live sessions + 1 test - Strategic decision-making optimization
 */
const MentorshipInfoPage = () => {
  // OVERVIEW SECTION
  // Defines the main value proposition and key highlights of the general mentorship program
  const overview = {
    title: "Personalized Physics Mentorship Program",
    description: "Physics can feel overwhelming — especially when you're trying to juggle board exam prep, entrance exams, and endless chapters. Our one-on-one mentorship program is designed to simplify your learning path. You'll get personalized guidance, structured study plans, and direct feedback from an experienced physics mentor.",
    highlights: [
      'Personalized study plans',
      'Weekly mentor sessions',
      'Progress tracking and feedback',
      'Exam strategy and time management'
    ]
  };

  // TARGET AUDIENCE SECTION
  // Defines who the general mentorship program is suitable for
  // NOTE: NEET program has more specific eligibility criteria (Class 12 students, repeaters, conditional Class 11 students)
  const targetAudience = [
    {
      icon: BookMarked,
      title: 'Board Exam Students',
      description: 'Class 11 & 12 students preparing for board exams (CBSE, ICSE, HSC, etc.)'
    },
    {
      icon: Target,
      title: 'Entrance Aspirants',
      description: 'Students preparing for JEE, NEET, CET, and other competitive exams'
    },
    {
      icon: Clock,
      title: 'Struggling Learners',
      description: 'Students who need help with conceptual clarity or time management'
    },
    {
      icon: Lightbulb,
      title: 'Focused Achievers',
      description: 'Ambitious students seeking personalized guidance and structured feedback'
    }
  ];

  // FEATURES SECTION
  // Core features of the general mentorship program
  // NOTE: NEET program focuses on strategic decision-making rather than teaching
  const features = [
    {
      icon: Users,
      title: 'Personalized Mentorship Sessions',
      description: 'One-hour one-on-one calls for deep guidance and strategy.'
    },
    {
      icon: BookMarked,
      title: 'Custom Study Plan',
      description: 'Assess your strengths, fix weak areas, and follow a tailored roadmap.'
    },
    {
      icon: Target,
      title: 'Exam Strategy & Time Management',
      titleHtml: 'Exam Strategy & <br/> Time Management',
      description: 'Learn how to approach questions smartly and manage pressure effectively.'
    },
    {
      icon: FileText,
      title: 'Full Revision Notes',
      description: 'Complete, structured notes covering your entire syllabus.'
    },
    {
      icon: CheckCircle,
      title: 'Practice Tests & Feedback',
      description: 'Mentor-guided assessments with detailed performance feedback.'
    },
    {
      icon: MessageCircle,
      title: 'Doubt Support',
      description: 'Priority chat/email access to clarify concepts between sessions.'
    }
  ];

  // PRICING SECTION
  // Current pricing for the general mentorship program
  // NOTE: This is different from NEET program pricing (not specified in the document)
  const pricing = {
    title: 'Mentorship Package',
    price: '₹4,500',
    originalPrice: '₹6,500',
    duration: '1 month',
    features: [
      '3 personalized one-on-one sessions',
      'Custom study plan & materials',
      'Performance tracking',
      'Progress reports',
      'Priority support',
      'Bonus: Free formula sheet'
    ]
  };

  // EXCLUSIONS SECTION
  // Clearly defines what's NOT included to set proper expectations
  // NOTE: NEET program has similar clarity about being a strategy program, not teaching
  const notIncluded = [
    {
      icon: X,
      title: 'Not Regular Tuition',
      description: 'This is not daily tutoring - we focus on strategic guidance'
    },
    {
      icon: Clock,
      title: 'Limited Support Hours',
      description: 'Support is available during working hours only'
    },
    {
      icon: FileText,
      title: 'Digital Materials Only',
      description: 'All notes and materials are provided as digital PDFs'
    },
    {
      icon: Zap,
      title: 'Only Mentorship Access',
      description: 'Complete Notes, DPPs, and Test Series are available as separate products'
    }
  ];

  // PROCESS STEPS SECTION
  // Defines the step-by-step journey for the general mentorship program
  // NOTE: NEET program has a more structured 11-day framework with specific daily strategic coverage
  const processSteps = [
    {
      icon: Calendar,
      title: 'Book Your Slot',
      description: 'Choose a convenient time via WhatsApp or email.'
    },
    {
      icon: FileText,
      title: 'Learning Profile',
      description: 'Complete a short form about your goals and challenges.'
    },
    {
      icon: Lightbulb,
      title: 'Diagnostic Session',
      description: 'Identify strengths, weaknesses, and create a plan.'
    },
    {
      icon: BrainCircuit,
      title: 'Deep Learning',
      description: 'Master concepts through focused sessions.'
    },
    {
      icon: CheckCircle,
      title: 'Review & Roadmap',
      description: 'Consolidate learning and plan next steps.'
    }
  ];

  // BENEFITS SECTION
  // Key value propositions and advantages of the general program
  const benefits = [
    {
      icon: Users,
      title: 'Expert Mentors',
      description: 'Learn from experienced physics educators'
    },
    {
      icon: Target,
      title: 'Focused Learning',
      description: 'Personalized approach for maximum efficiency'
    },
    {
      icon: BarChart2,
      title: 'Track Progress',
      description: 'Data-driven insights into your improvement'
    },
    {
      icon: Zap,
      title: 'Real Results',
      description: 'Proven improvement in understanding and scores'
    }
  ];

  // ADD-ONS SECTION
  // Additional resources that can be purchased separately
  // NOTE: NEET program materials are included as part of the intellectual property
  const addOns = [
    {
      icon: BookOpen,
      title: 'Entrance Notes Bundle',
      price: '₹699',
      features: ['Complete JEE/NEET notes', 'Important formulas', 'Solved examples']
    },
    {
      icon: Award,
      title: 'Test Series',
      price: 'Starts at ₹1,499',
      features: ['Chapter-wise tests', 'Full-length mocks', 'Detailed solutions']
    }
  ];

  // TESTIMONIALS SECTION
  // Social proof to build trust and credibility
  // NOTE: NEET program would benefit from specific testimonials about score improvement
  const testimonials = [
    {
      quote: '"After just two sessions, my confidence in optics went up massively!"',
      author: 'Rahul K., JEE Aspirant'
    },
    {
      quote: '"The notes and study plan made revision so much easier before boards."',
      author: 'Priya M., 12th CBSE'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Mentorship Program - Physics Notebook</title>
        <meta 
          name="description" 
          content="Personalized 1:1 physics mentorship program for board and entrance exam preparation. Get expert guidance, study plans, and performance tracking." 
        />
      </Helmet>

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <Button variant="ghost" asChild className="text-gray-600 hover:bg-gray-100">
          <Link to="/solutions" className="flex items-center gap-2 group">
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
            <span>Back to Solutions</span>
          </Link>
        </Button>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-600 py-16 md:py-24 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center md:text-left"
            >
              <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-6">
                One-on-One Physics Mentorship
              </h1>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                {overview.description}
              </p>
              <div className="flex flex-wrap justify-center md:justify-start gap-4 mb-8">
                {overview.highlights.map((item, index) => (
                  <span 
                    key={index}
                    className="inline-flex items-center bg-blue-500/20 px-4 py-2 rounded-full text-sm font-medium"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {item}
                  </span>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <Button 
                  asChild 
                  className="bg-white text-blue-700 hover:bg-blue-50 px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all"
                >
                  <a href="https://forms.gle/NrvXCFkKXUmwW9K59" target="_blank" rel="noopener noreferrer">
                    Book Your Consultation
                  </a>
                </Button>
                <Button 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-medium"
                  asChild
                >
                  <a href="#how-it-works">
                    How It Works
                  </a>
                </Button>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-1 shadow-2xl">
                <div className="bg-white rounded-xl overflow-hidden shadow-lg">
                  <div className="p-4 bg-gray-100 flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="p-6">
                    <div className="aspect-video bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center">
                      <Users className="w-16 h-16 text-blue-600" />
                    </div>
                    <div className="mt-6 space-y-4">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <MessageCircle className="w-5 h-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg rounded-tl-none">
                          <p className="text-gray-800">Hi! I'm your physics mentor. How can I help you today?</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-indigo-600" />
                          </div>
                        </div>
                        <div className="bg-indigo-600 text-white p-3 rounded-lg rounded-tl-none">
                          <p>I'm struggling with understanding physics. Can you help?</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-yellow-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-pink-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
              <div className="absolute -bottom-8 right-20 w-24 h-24 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="py-16 bg-white" id="who-its-for">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              Who Should Join
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Perfect For Ambitious Physics Students
            </h2>
            <div className="w-20 h-1 bg-blue-600 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {targetAudience.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6 border border-gray-100"
              >
                <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center mb-4 text-blue-600">
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 
                  className="text-xl font-semibold text-gray-900 mb-2"
                  dangerouslySetInnerHTML={{ __html: item.titleHtml || item.title }}
                />
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50" id="features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              Program Features
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Everything You Need to Succeed
            </h2>
            <p className="text-xl text-gray-600">
              Our comprehensive mentorship program is designed to give you the tools and support you need to excel in physics.
            </p>
            <div className="w-20 h-1 bg-blue-600 mx-auto mt-6"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-all p-6 border border-gray-100 hover:border-blue-100"
              >
                <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center mb-4 text-blue-600 group-hover:bg-blue-100 group-hover:text-blue-700 transition-colors">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-white" id="pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              Pricing & Plans
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600">
              Choose the perfect plan that fits your needs and budget.
            </p>
            <div className="w-20 h-1 bg-blue-600 mx-auto mt-6"></div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
              <div className="p-8 md:p-10">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{pricing.title}</h3>
                    <p className="text-gray-600 mt-1">Perfect for focused exam preparation</p>
                  </div>
                  <div className="mt-4 md:mt-0 text-center md:text-right">
                    <div className="text-4xl font-bold text-gray-900">{pricing.price}
                      <span className="text-base font-normal text-gray-500 ml-2">/ {pricing.duration}</span>
                    </div>
                    {pricing.originalPrice && (
                      <div className="text-gray-500 line-through">{pricing.originalPrice}</div>
                    )}
                    <div className="text-sm text-green-600 font-medium mt-1">
                      Limited time offer
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                  {pricing.features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <Check className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="text-center">
                  <Button 
                    asChild 
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-medium w-full md:w-auto"
                  >
                    <a href="https://forms.gle/NrvXCFkKXUmwW9K59" target="_blank" rel="noopener noreferrer">
                      Get Started Now
                    </a>
                  </Button>
                  <p className="text-sm text-gray-500 mt-3">
                    No credit card required. Cancel anytime.
                  </p>
                </div>
              </div>
              
              <div className="bg-gray-50 px-8 py-4 border-t border-gray-200">
                <div className="flex items-center justify-center text-sm text-gray-500">
                  <MessageCircle className="w-4 h-4 mr-2 text-gray-400" />
                  Need help choosing? <a href="#contact" className="text-blue-600 hover:text-blue-800 ml-1 font-medium">Contact us</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Not Included Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-2 bg-red-100 text-red-700 rounded-full text-sm font-medium mb-4">
              What's Not Included
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Clear Expectations
            </h2>
            <p className="text-xl text-gray-600">
              We believe in complete transparency about what this program does and doesn't include.
            </p>
            <div className="w-20 h-1 bg-red-500 mx-auto mt-6"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {notIncluded.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center mb-4 text-red-500">
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 
                  className="text-xl font-semibold text-gray-900 mb-2"
                  dangerouslySetInnerHTML={{ __html: item.titleHtml || item.title }}
                />
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-white" id="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4">
              Our Process
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              How Our Mentorship Works
            </h2>
            <p className="text-xl text-gray-600">
              A simple, structured approach to help you achieve your physics goals.
            </p>
            <div className="w-20 h-1 bg-blue-600 mx-auto mt-6"></div>
          </div>

          <div className="relative max-w-4xl mx-auto">
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-indigo-500 -ml-px"></div>
            
            <div className="space-y-12">
              {processSteps.map((step, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative"
                >
                  <div className={`flex flex-col md:flex-row items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 flex-shrink-0 relative z-10">
                      <div className="w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center">
                        <step.icon className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                    <div className={`mt-6 md:mt-0 md:flex-1 md:px-8 ${index % 2 === 0 ? 'md:text-left md:pr-12' : 'md:text-right md:pl-12'}`}>
                      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                        <div className="flex items-center md:hidden mb-4">
                          <div className="w-8 h-1 bg-blue-100 rounded-full mr-2"></div>
                          <span className="text-xs font-medium text-blue-600">Step {index + 1}</span>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{step.title}</h3>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-2 bg-white text-blue-700 rounded-full text-sm font-medium shadow-sm mb-4">
              Why Choose Us
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              The Physics Notebook Advantage
            </h2>
            <p className="text-xl text-gray-600">
              What makes our mentorship program different and effective.
            </p>
            <div className="w-20 h-1 bg-blue-600 mx-auto mt-6"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center mb-4 text-blue-600">
                  <benefit.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-Ons Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
              Additional Resources
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Enhance Your Learning
            </h2>
            <p className="text-xl text-gray-600">
              Supplement your mentorship with these valuable resources.
            </p>
            <div className="w-20 h-1 bg-purple-600 mx-auto mt-6"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {addOns.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600 mr-4">
                      <item.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 
                        className="text-xl font-semibold text-gray-900"
                        dangerouslySetInnerHTML={{ __html: item.titleHtml || item.title }}
                      />
                      <p className="text-purple-600 font-medium">{item.price}</p>
                    </div>
                  </div>
                  <ul className="space-y-2 mt-4">
                    {item.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <Check className="w-4 h-4 text-green-500 mt-1 mr-2 flex-shrink-0" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MentorshipInfoPage;
